import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, BanknotesIcon } from '@heroicons/react/24/outline';

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
};

const actorLabel = (person) => {
  if (!person?.name) return 'N/A';
  return person.employeeId ? `${person.name} (${person.employeeId})` : person.name;
};

const today = new Date().toISOString().split('T')[0];

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const StatCard = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const statusBadge = (status) => {
  switch (status) {
    case 'approved':
      return 'status-approved';
    case 'paid':
      return 'status-paid';
    case 'rejected':
      return 'status-rejected';
    default:
      return 'status-pending';
  }
};

const OfficeCategoryLedger = () => {
  const { categoryName } = useParams();
  const { canAccess } = useAuth();
  const category = decodeURIComponent(categoryName || '');
  const [expenses, setExpenses] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: today
  });

  const fetchCategoryExpenses = useCallback(async () => {
    if (!canAccess('office')) return;

    try {
      setLoading(true);
      const params = new URLSearchParams({ category, limit: '500' });
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      const response = await api.get(`/api/office/expenses?${params.toString()}`);
      setExpenses(response.data.expenses || []);
      setTotalAmount(response.data.totalAmount || 0);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch category statement');
    } finally {
      setLoading(false);
    }
  }, [canAccess, category, filters]);

  useEffect(() => {
    fetchCategoryExpenses();
  }, [fetchCategoryExpenses]);

  const summary = useMemo(() => {
    return expenses.reduce(
      (acc, expense) => {
        acc[expense.status] = (acc[expense.status] || 0) + 1;
        return acc;
      },
      { pending: 0, approved: 0, rejected: 0, paid: 0 }
    );
  }, [expenses]);

  const exportCsv = () => {
    const rows = [
      ['Category', 'Date', 'Purpose', 'Description', 'Vendor', 'Receipt Number', 'Status', 'Amount', 'Billed By', 'Approved By', 'Paid By', 'Reference Number'],
      ...expenses.map((expense) => [
        category,
        formatDate(expense.date),
        expense.purpose,
        expense.description,
        expense.vendor,
        expense.receiptNumber,
        expense.status,
        Number(expense.amount || 0),
        actorLabel(expense.billedBy),
        actorLabel(expense.approvedBy),
        actorLabel(expense.paidBy),
        expense.referenceNumber
      ])
    ];

    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `office-${category}-${filters.dateFrom || 'all'}-${filters.dateTo || 'all'}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${expenses.length} office statement rows`);
  };

  const printStatement = () => {
    window.print();
  };

  if (!canAccess('office')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Link to="/office" className="text-primary-600 hover:text-primary-900 inline-flex items-center text-sm mb-2 no-print">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Office Expenses
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{category}</h1>
          <p className="text-gray-600">Office expense statement for this category</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row no-print">
          <button type="button" onClick={exportCsv} className="btn-secondary">Export CSV</button>
          <button type="button" onClick={printStatement} className="btn-secondary">Print</button>
          <button type="button" onClick={fetchCategoryExpenses} className="btn-secondary">Refresh</button>
        </div>
      </div>

      <div className="print-only">
        <p className="text-sm text-gray-600">
          Generated {new Date().toLocaleString()} | Range {filters.dateFrom || 'Beginning'} to {filters.dateTo || 'Today'}
        </p>
      </div>

      <div className="card p-4 no-print">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
            className="input-field"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="paid">Paid</option>
            <option value="rejected">Rejected</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateFrom: event.target.value }))}
            className="input-field"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => setFilters((prev) => ({ ...prev, dateTo: event.target.value }))}
            className="input-field"
          />
          <button
            type="button"
            onClick={() => setFilters({ status: '', dateFrom: '', dateTo: '' })}
            className="btn-secondary"
          >
            All Time
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <StatCard label="Total Spend" value={formatCurrency(totalAmount)} tone="text-red-600" />
        <StatCard label="Entries" value={expenses.length} />
        <StatCard label="Pending" value={summary.pending || 0} tone="text-yellow-600" />
        <StatCard label="Approved" value={summary.approved || 0} tone="text-green-600" />
        <StatCard label="Paid" value={summary.paid || 0} tone="text-blue-600" />
      </div>

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <BanknotesIcon className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold text-gray-900">Bills and Payments</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expense</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor / Receipt</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center">
                    <div className="spinner mx-auto"></div>
                  </td>
                </tr>
              ) : expenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">No bills found for this category</td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense._id}>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(expense.date)}</td>
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900">{expense.purpose}</p>
                      <p className="text-sm text-gray-500 max-w-lg truncate">{expense.description}</p>
                      {expense.notes && <p className="text-xs text-gray-400 max-w-lg truncate">{expense.notes}</p>}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>{expense.vendor || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{expense.receiptNumber || 'No receipt'}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={statusBadge(expense.status)}>{expense.status}</span>
                      <p className="text-xs text-gray-500 mt-1">Billed by {actorLabel(expense.billedBy)}</p>
                      {expense.approvedBy?.name && (
                        <p className="text-xs text-gray-500 mt-1">Approved by {actorLabel(expense.approvedBy)}</p>
                      )}
                      {expense.paidBy?.name && (
                        <p className="text-xs text-gray-500 mt-1">Paid by {actorLabel(expense.paidBy)}</p>
                      )}
                      {expense.referenceNumber && (
                        <p className="text-xs text-gray-500 mt-1">Ref: {expense.referenceNumber}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(expense.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OfficeCategoryLedger;
